import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Search, Filter, Download, Camera, BellRing, TrendingUp, TrendingDown,
  Minus, Sparkles, Activity, ChevronRight, X, ShieldAlert, LineChart,
  History, Settings2, ScrollText,
} from "lucide-react";
import PortfolioBrandDrilldown from "./PortfolioBrandDrilldown";
import PortfolioSnapshotsDialog from "./PortfolioSnapshotsDialog";
import PulseAlertConfigDialog from "./PulseAlertConfigDialog";
import PulseAlertsSettingsDialog from "./PulseAlertsSettingsDialog";
import PulseAlertEventsDialog from "./PulseAlertEventsDialog";
import StrategyChangeFeedCard from "./StrategyChangeFeedCard";
import EscalationHeatmapCard from "./EscalationHeatmapCard";
import WaHotThreadsCard from "./WaHotThreadsCard";
import GtmHealthTile from "./GtmHealthTile";

const BAND_TONE = {
  accelerating: { bg: "bg-emerald-500", text: "text-emerald-900", tile: "bg-emerald-50 border-emerald-400", pill: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  steady:       { bg: "bg-sky-500",     text: "text-sky-900",     tile: "bg-sky-50 border-sky-400",         pill: "bg-sky-100 text-sky-800 border-sky-300" },
  cooling:      { bg: "bg-amber-500",   text: "text-amber-900",   tile: "bg-amber-50 border-amber-400",     pill: "bg-amber-100 text-amber-800 border-amber-300" },
  stalled:      { bg: "bg-rose-500",    text: "text-rose-900",    tile: "bg-rose-50 border-rose-400",       pill: "bg-rose-100 text-rose-800 border-rose-300" },
};

function Sparkline({ points, color = "#0f172a", w = 80, h = 24 }) {
  if (!points || points.length < 2) return <div className="h-6" />;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = w / (points.length - 1);
  const coords = points.map((p, i) => `${(i * step).toFixed(1)},${(h - ((p - min) / range) * h).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} className="shrink-0">
      <polyline points={coords} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function DeltaChip({ delta }) {
  if (delta === null || delta === undefined) return <span className="text-[10px] text-muted-foreground">—</span>;
  const positive = delta > 2;
  const negative = delta < -2;
  const cls = positive ? "text-emerald-700 bg-emerald-50 border-emerald-300"
    : negative ? "text-rose-700 bg-rose-50 border-rose-300"
    : "text-muted-foreground bg-muted/40 border-border";
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 border ${cls}`}>
      <Icon className="w-3 h-3" /> {delta > 0 ? "+" : ""}{delta}
    </span>
  );
}

function BrandTile({ row, onClick, onAlertConfig }) {
  const tone = BAND_TONE[row.band] || BAND_TONE.steady;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      className={`relative border-2 ${tone.tile} p-3 text-left hover:shadow-md transition-all group focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer`}
      data-testid={`portfolio-pulse-tile-${row.brand_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate text-sm">{row.brand_name}</div>
          <div className="text-[10px] text-muted-foreground truncate">{row.industry || "—"}</div>
        </div>
        {row.alert_triggered && (
          <span className="shrink-0" title="Alert triggered">
            <ShieldAlert className="w-4 h-4 text-rose-600" />
          </span>
        )}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`text-3xl font-mono font-black ${tone.text}`} data-testid={`portfolio-pulse-tile-score-${row.brand_id}`}>
          {row.current}
        </span>
        <span className={`text-[9px] uppercase tracking-widest ${tone.text} font-bold`}>{row.band}</span>
      </div>
      <div className="mt-1 flex items-center justify-between">
        <DeltaChip delta={row.delta} />
        <Sparkline points={row.scores} color={row.band_color} w={72} h={20} />
      </div>
      <div className="mt-2 grid grid-cols-4 gap-1 text-[9px]">
        <DimBar label="H" val={row.dim_health} />
        <DimBar label="AI" val={row.dim_confidence} />
        <DimBar label="Act" val={row.dim_activity} />
        <DimBar label="Vel" val={row.dim_velocity} />
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onAlertConfig(row); }}
        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 border border-border bg-white/95 hover:border-primary hover:text-primary transition-opacity"
        title="Alert thresholds"
        data-testid={`portfolio-pulse-tile-alerts-${row.brand_id}`}>
        <BellRing className="w-3 h-3" />
      </button>
    </div>
  );
}

function DimBar({ label, val }) {
  const v = val ?? 0;
  const tone = v >= 75 ? "bg-emerald-500" : v >= 55 ? "bg-sky-500" : v >= 35 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="flex items-center gap-1">
      <span className="text-[8px] text-muted-foreground w-6">{label}</span>
      <div className="flex-1 h-1 bg-white/70 border border-border relative overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, tone = "" }) {
  return (
    <div className={`border border-border p-3 bg-card ${tone}`}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className="text-2xl font-mono font-black mt-1">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function MoverRow({ row, onClick }) {
  const tone = BAND_TONE[row.band] || BAND_TONE.steady;
  return (
    <button type="button" onClick={onClick}
      className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-muted/40 border border-transparent hover:border-border text-left"
      data-testid={`portfolio-mover-${row.brand_id}`}>
      <span className={`w-1.5 h-6 ${tone.bg}`} />
      <span className="text-xs font-medium truncate flex-1">{row.brand_name}</span>
      <span className="text-xs font-mono w-8 text-right">{row.current}</span>
      <DeltaChip delta={row.delta} />
      <ChevronRight className="w-3 h-3 text-muted-foreground" />
    </button>
  );
}

// Iter 188 · SLA burn-down card
function SlaBurndownCard({ wsId, onDrill }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState(4);
  useEffect(() => {
    if (!wsId) return;
    setLoading(true);
    // Fetch fresh each time weeks/ws changes
    import("@/lib/api").then(({ default: api }) => {
      api.get(`/workspaces/${wsId}/portfolio/sla-burndown`, { params: { weeks } })
          .then((r) => setData(r.data))
          .catch(() => setData(null))
          .finally(() => setLoading(false));
    });
  }, [wsId, weeks]);

  if (loading) return <div className="border border-border bg-card p-3 text-xs text-muted-foreground" data-testid="portfolio-sla-burndown">Loading SLA burn-down…</div>;
  if (!data) return null;

  const maxY = Math.max(1, ...data.series.map((w) => w.planned || 0));

  return (
    <div className="border border-border bg-card p-3" data-testid="portfolio-sla-burndown">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Content SLA burn-down</div>
          <div className="text-[10px] text-muted-foreground">Cross-brand delivery health for the last {weeks} week(s)</div>
        </div>
        <div className="flex items-center gap-1">
          {[4, 8, 12].map((w) => (
            <button key={w} type="button" onClick={() => setWeeks(w)}
                      className={`h-6 px-2 text-[10px] border ${weeks === w ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}
                      data-testid={`portfolio-sla-window-${w}`}>
              {w}w
            </button>
          ))}
        </div>
      </div>
      {/* At-risk headline */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div className={`border p-2 ${data.breached_now > 0 ? "border-rose-500 bg-rose-50" : "border-border bg-muted/20"}`}
               data-testid="portfolio-sla-breached">
          <div className="text-[9px] uppercase tracking-widest font-bold text-rose-800">Breached now</div>
          <div className="text-2xl font-mono font-black text-rose-900">{data.breached_now}</div>
        </div>
        <div className={`border p-2 ${data.at_risk_now > 0 ? "border-amber-500 bg-amber-50" : "border-border bg-muted/20"}`}
               data-testid="portfolio-sla-at-risk">
          <div className="text-[9px] uppercase tracking-widest font-bold text-amber-800">At risk (7d)</div>
          <div className="text-2xl font-mono font-black text-amber-900">{data.at_risk_now}</div>
        </div>
        <div className="border border-border p-2 bg-emerald-50">
          <div className="text-[9px] uppercase tracking-widest font-bold text-emerald-800">Met (window)</div>
          <div className="text-2xl font-mono font-black text-emerald-900">{data.series.reduce((s, w) => s + w.met, 0)}</div>
        </div>
        <div className="border border-border p-2 bg-card">
          <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground">Missed (window)</div>
          <div className="text-2xl font-mono font-black">{data.series.reduce((s, w) => s + w.missed, 0)}</div>
        </div>
      </div>
      {/* Stacked bar chart */}
      <div className="flex items-end gap-1 h-28">
        {data.series.map((w, i) => {
          const total = w.planned || 1;
          const hMet = (w.met / total) * 100;
          const hMissed = (w.missed / total) * 100;
          const hOpen = (w.still_open / total) * 100;
          return (
            <div key={i} className="flex-1 flex flex-col items-center" title={`${w.week_start} → ${w.week_end}\nPlanned: ${w.planned}\nMet: ${w.met}\nMissed: ${w.missed}\nStill open: ${w.still_open}`}
                    data-testid={`portfolio-sla-week-${i}`}>
              <div className="w-full h-full flex flex-col justify-end border border-border">
                {hOpen > 0 && <div className="bg-amber-400" style={{ height: `${hOpen}%` }} />}
                {hMissed > 0 && <div className="bg-rose-500" style={{ height: `${hMissed}%` }} />}
                {hMet > 0 && <div className="bg-emerald-500" style={{ height: `${hMet}%` }} />}
              </div>
              <div className="text-[9px] text-muted-foreground mt-1 font-mono">{w.week_end.slice(5)}</div>
              <div className="text-[9px] font-bold">{w.planned}</div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 mt-2 flex-wrap text-[10px]">
        <div className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 inline-block" /> Met</div>
        <div className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-rose-500 inline-block" /> Missed</div>
        <div className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 inline-block" /> Still open</div>
        <span className="ml-auto text-muted-foreground">Numbers below bar = total planned that week</span>
      </div>
      {/* At-risk drill list */}
      {data.at_risk_items?.length > 0 && (
        <div className="mt-3 pt-2 border-t border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">At-risk items · click to drill into brand</div>
          <div className="space-y-1">
            {data.at_risk_items.slice(0, 6).map((r) => (
              <button key={r.id} type="button"
                        onClick={() => onDrill && onDrill(r.brand_id)}
                        className={`w-full flex items-center gap-2 text-xs px-2 py-1 border ${r._breached ? "border-rose-300 bg-rose-50" : "border-amber-300 bg-amber-50"} hover:border-primary text-left`}
                        data-testid={`portfolio-sla-at-risk-${r.id}`}>
                <span className={`w-2 h-2 ${r._breached ? "bg-rose-500" : "bg-amber-500"}`} />
                <span className="font-medium truncate flex-1">{r.brand_name}</span>
                <span className="text-muted-foreground truncate max-w-[24ch]">{r.topic_title}</span>
                <span className="font-mono text-[10px]">{r._due.slice(0, 10)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


export default function PortfolioPulseTab() {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [band, setBand] = useState("_all");
  const [status, setStatus] = useState("_all");
  const [clientId, setClientId] = useState("_all");
  const [industry, setIndustry] = useState("_all");
  const [minScore, setMinScore] = useState("");
  const [deltaDir, setDeltaDir] = useState("_all");
  const [sortMode, setSortMode] = useState("rank");   // rank | current | delta | name
  const [clients, setClients] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [drillBrandId, setDrillBrandId] = useState(null);
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);
  const [alertBrand, setAlertBrand] = useState(null);
  const [alertsSettingsOpen, setAlertsSettingsOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (band !== "_all") p.set("band", band);
    if (status !== "_all") p.set("status", status);
    if (clientId !== "_all") p.set("client_id", clientId);
    if (industry !== "_all") p.set("industry", industry);
    if (minScore) p.set("min_score", minScore);
    if (deltaDir !== "_all") p.set("delta_dir", deltaDir);
    try {
      const r = await api.get(`/workspaces/${activeWorkspaceId}/brands/pulse/portfolio?${p.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load portfolio pulse"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [activeWorkspaceId, band, status, clientId, industry, deltaDir]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/clients`).then(r => setClients(r.data || [])).catch(() => {});
    api.get(`/reference/industries`).then(r => setIndustries(r.data?.industries || [])).catch(() => {});
  }, [activeWorkspaceId]);

  const rows = useMemo(() => {
    const arr = [...(data?.rows || [])];
    if (sortMode === "current") arr.sort((a, b) => b.current - a.current);
    else if (sortMode === "delta") arr.sort((a, b) => (b.delta || 0) - (a.delta || 0));
    else if (sortMode === "name") arr.sort((a, b) => (a.brand_name || "").localeCompare(b.brand_name || ""));
    // 'rank' preserves server-side ordering (pulse × delta)
    return arr;
  }, [data, sortMode]);

  const agg = data?.aggregate || {};
  const dist = agg.distribution || {};
  const distPct = agg.distribution_pct || {};

  const capturePulse = async () => {
    setCapturing(true);
    const t = toast.loading("Capturing pulse for all brands…");
    try {
      const r = await api.post(`/workspaces/${activeWorkspaceId}/brands/pulse/snapshot`);
      toast.success(`Captured ${r.data.captured} of ${r.data.total}`, { id: t });
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Capture failed"), { id: t }); }
    finally { setCapturing(false); }
  };

  const downloadPdf = async () => {
    if (!activeWorkspaceId) return;
    const t = toast.loading("Generating PDF…");
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (band !== "_all") p.set("band", band);
      if (status !== "_all") p.set("status", status);
      if (clientId !== "_all") p.set("client_id", clientId);
      if (industry !== "_all") p.set("industry", industry);
      if (minScore) p.set("min_score", minScore);
      if (deltaDir !== "_all") p.set("delta_dir", deltaDir);
      const r = await api.get(
        `/workspaces/${activeWorkspaceId}/brands/pulse/portfolio.pdf?${p.toString()}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `portfolio-pulse-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded", { id: t });
    } catch (e) { toast.error(apiErrorMessage(e, "PDF export failed"), { id: t }); }
  };

  const saveSnapshot = async () => {
    const name = window.prompt("Snapshot name?", `Portfolio · ${new Date().toDateString()}`);
    if (!name) return;
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/brands/pulse/portfolio-snapshots`, { name });
      toast.success("Portfolio snapshot saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const resetFilters = () => {
    setQ(""); setBand("_all"); setStatus("_all"); setClientId("_all");
    setIndustry("_all"); setMinScore(""); setDeltaDir("_all");
    // Auto-reload after state batches settle
    setTimeout(() => load(), 0);
  };

  const hasFilters = q || band !== "_all" || status !== "_all" || clientId !== "_all" || industry !== "_all" || minScore || deltaDir !== "_all";

  return (
    <div className="px-4 sm:px-6 md:px-10 pb-10 pt-6 space-y-4" data-testid="portfolio-pulse-tab">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <LineChart className="w-4 h-4 text-primary" />
            <h2 className="font-semibold text-sm">Portfolio Pulse</h2>
            <span className="text-xs text-muted-foreground">· {data?.total_filtered ?? 0} of {data?.total_all ?? 0}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="rounded-none h-8" onClick={capturePulse} disabled={capturing}
                  data-testid="portfolio-pulse-capture-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {capturing ? "Capturing…" : "Capture pulse"}
          </Button>
          <Button variant="outline" size="sm" className="rounded-none h-8" onClick={saveSnapshot}
                  data-testid="portfolio-pulse-save-snapshot-btn">
            <Camera className="w-3.5 h-3.5 mr-1.5" /> Save snapshot
          </Button>
          <Button variant="outline" size="sm" className="rounded-none h-8" onClick={() => setSnapshotsOpen(true)}
                  data-testid="portfolio-pulse-snapshots-btn">
            <History className="w-3.5 h-3.5 mr-1.5" /> Snapshots
          </Button>
          <Button variant="outline" size="sm" className="rounded-none h-8" onClick={() => setAlertsSettingsOpen(true)}
                  data-testid="portfolio-pulse-alerts-settings-btn">
            <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Alerts settings
          </Button>
          <Button variant="outline" size="sm" className="rounded-none h-8" onClick={() => setEventsOpen(true)}
                  data-testid="portfolio-pulse-events-btn">
            <ScrollText className="w-3.5 h-3.5 mr-1.5" /> Events log
          </Button>
          <Button variant="outline" size="sm" className="rounded-none h-8" onClick={downloadPdf}
                  data-testid="portfolio-pulse-pdf-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-border bg-card p-3">
        <div className="flex items-center gap-2 flex-wrap" data-testid="portfolio-pulse-filters">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
            <Input className="pl-9 h-9 rounded-none" placeholder="Search brands…" value={q}
                     onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                     data-testid="portfolio-pulse-search" />
          </div>
          <Select value={band} onValueChange={setBand}>
            <SelectTrigger className="h-9 w-40 rounded-none" data-testid="portfolio-pulse-band-filter"><SelectValue placeholder="Band" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All bands</SelectItem>
              <SelectItem value="accelerating">Accelerating (75+)</SelectItem>
              <SelectItem value="steady">Steady (55-74)</SelectItem>
              <SelectItem value="cooling">Cooling (35-54)</SelectItem>
              <SelectItem value="stalled">Stalled (0-34)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-36 rounded-none" data-testid="portfolio-pulse-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="h-9 w-40 rounded-none" data-testid="portfolio-pulse-client-filter"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All clients</SelectItem>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger className="h-9 w-40 rounded-none" data-testid="portfolio-pulse-industry-filter"><SelectValue placeholder="Industry" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="_all">All industries</SelectItem>
              {industries.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={deltaDir} onValueChange={setDeltaDir}>
            <SelectTrigger className="h-9 w-32 rounded-none" data-testid="portfolio-pulse-delta-filter"><SelectValue placeholder="Δ" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Any Δ</SelectItem>
              <SelectItem value="up">Rising</SelectItem>
              <SelectItem value="down">Dropping</SelectItem>
              <SelectItem value="flat">Flat</SelectItem>
            </SelectContent>
          </Select>
          <Input type="number" placeholder="Min score" className="h-9 w-28 rounded-none"
                   value={minScore} onChange={(e) => setMinScore(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                   data-testid="portfolio-pulse-min-score" />
          <Button variant="outline" size="sm" className="rounded-none h-9" onClick={load}
                  data-testid="portfolio-pulse-apply-filters-btn">
            <Filter className="w-3.5 h-3.5 mr-1.5" /> Apply
          </Button>
          {hasFilters && (
            <Button variant="ghost" size="sm" className="rounded-none h-9 text-xs" onClick={resetFilters}
                    data-testid="portfolio-pulse-reset-filters-btn">
              <X className="w-3 h-3 mr-1" /> Reset
            </Button>
          )}
          <div className="ml-auto flex items-center gap-1">
            <span className="text-[10px] text-muted-foreground">Sort:</span>
            <Select value={sortMode} onValueChange={setSortMode}>
              <SelectTrigger className="h-8 w-40 rounded-none text-xs" data-testid="portfolio-pulse-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rank">Rank (pulse × Δ)</SelectItem>
                <SelectItem value="current">Highest pulse</SelectItem>
                <SelectItem value="delta">Biggest Δ</SelectItem>
                <SelectItem value="name">Name (A→Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2" data-testid="portfolio-pulse-kpis">
        <KpiCard label="Brands" value={agg.total ?? 0} sub={`${data?.total_all ?? 0} total`} />
        <KpiCard label="Avg pulse" value={agg.avg ?? 0} sub={`median ${agg.median ?? 0}`} />
        <KpiCard label="Movers ↑" value={agg.movers_up ?? 0} sub="Δ > +2" tone="border-emerald-400/60" />
        <KpiCard label="Movers ↓" value={agg.movers_down ?? 0} sub="Δ < -2" tone="border-rose-400/60" />
        <KpiCard label="Alerts" value={data?.alerts_triggered_count ?? 0} sub="triggered now" tone="border-amber-400/60" />
        <KpiCard label="Accelerating" value={dist.accelerating ?? 0} sub={`${distPct.accelerating ?? 0}%`} tone="border-emerald-400/60" />
      </div>

      {/* Distribution bar */}
      <div className="border border-border bg-card p-3" data-testid="portfolio-pulse-distribution">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Band distribution</div>
          <div className="text-[10px] text-muted-foreground">Weighted 0-100 momentum</div>
        </div>
        <div className="h-3 flex border border-border overflow-hidden">
          {["accelerating", "steady", "cooling", "stalled"].map((b) => (
            <div key={b} className={`${BAND_TONE[b].bg}`}
                   style={{ width: `${distPct[b] || 0}%` }}
                   title={`${b}: ${dist[b] || 0} (${distPct[b] || 0}%)`} />
          ))}
        </div>
        <div className="flex items-center gap-3 mt-2 flex-wrap text-[10px]">
          {["accelerating", "steady", "cooling", "stalled"].map((b) => (
            <div key={b} className="inline-flex items-center gap-1">
              <span className={`w-2 h-2 ${BAND_TONE[b].bg}`} />
              <span className="uppercase font-semibold">{b}</span>
              <span className="text-muted-foreground">· {dist[b] || 0} ({distPct[b] || 0}%)</span>
            </div>
          ))}
        </div>
      </div>

      {/* Iter 188 · SLA burn-down strip */}
      <SlaBurndownCard wsId={activeWorkspaceId} onDrill={setDrillBrandId} />

      {/* Iter 196 · Hot WhatsApp threads (unassigned) */}
      <WaHotThreadsCard wsId={activeWorkspaceId} />

      {/* Iter 198a · GTM Health tile */}
      <GtmHealthTile wsId={activeWorkspaceId} />

      {/* Iter 191 · Strategy Change Feed */}
      <StrategyChangeFeedCard wsId={activeWorkspaceId} />

      {/* Iter 194 · Escalation Heat-map */}
      <EscalationHeatmapCard wsId={activeWorkspaceId} />

      {/* Alerts strip */}
      {(data?.alerts_triggered || []).length > 0 && (
        <div className="border-2 border-rose-400 bg-rose-50 p-3" data-testid="portfolio-pulse-alerts-strip">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-4 h-4 text-rose-600" />
            <span className="text-xs font-semibold text-rose-900">{data.alerts_triggered.length} alert(s) triggered</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {data.alerts_triggered.slice(0, 12).map((a) => (
              <button key={a.brand_id} type="button" onClick={() => setDrillBrandId(a.brand_id)}
                        className="inline-flex items-center gap-1 border border-rose-300 bg-white hover:border-rose-500 px-1.5 py-0.5 text-[10px]"
                        title={a.reasons.join("; ")}
                        data-testid={`portfolio-pulse-alert-chip-${a.brand_id}`}>
                <ShieldAlert className="w-3 h-3 text-rose-600" />
                <span className="font-medium">{a.brand_name}</span>
                <span className="text-rose-700 font-mono">· {a.current}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Layout: heatmap + movers */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Heatmap */}
        <div>
          {loading ? (
            <div className="text-sm text-muted-foreground p-8 text-center">Loading portfolio…</div>
          ) : rows.length === 0 ? (
            <div className="border border-dashed border-border p-8 text-center" data-testid="portfolio-pulse-empty">
              <Activity className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <div className="text-sm font-medium">No brands match your filters</div>
              <div className="text-xs text-muted-foreground mt-1">Capture pulse first or adjust filters.</div>
              {hasFilters && (
                <Button variant="outline" size="sm" className="rounded-none mt-3" onClick={resetFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="portfolio-pulse-grid">
              {rows.map((r) => (
                <BrandTile key={r.brand_id} row={r}
                             onClick={() => setDrillBrandId(r.brand_id)}
                             onAlertConfig={(b) => setAlertBrand(b)} />
              ))}
            </div>
          )}
        </div>

        {/* Movers */}
        <div className="border border-border bg-card p-3 self-start" data-testid="portfolio-pulse-movers">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Movers this week</div>
            <Badge variant="outline" className="rounded-none text-[9px]">Δ vs prev capture</Badge>
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-[10px] font-semibold text-emerald-700 mb-1">↑ Top gainers</div>
              {(data?.movers?.gainers || []).length === 0 && <div className="text-[10px] text-muted-foreground italic">No gainers</div>}
              {(data?.movers?.gainers || []).map((r) => (
                <MoverRow key={r.brand_id} row={r} onClick={() => setDrillBrandId(r.brand_id)} />
              ))}
            </div>
            <div>
              <div className="text-[10px] font-semibold text-rose-700 mb-1">↓ Biggest droppers</div>
              {(data?.movers?.droppers || []).length === 0 && <div className="text-[10px] text-muted-foreground italic">No droppers</div>}
              {(data?.movers?.droppers || []).map((r) => (
                <MoverRow key={r.brand_id} row={r} onClick={() => setDrillBrandId(r.brand_id)} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {drillBrandId && (
        <PortfolioBrandDrilldown wsId={activeWorkspaceId} brandId={drillBrandId}
                                     onClose={() => setDrillBrandId(null)}
                                     onOpenAlertConfig={(b) => { setDrillBrandId(null); setAlertBrand(b); }} />
      )}
      {snapshotsOpen && (
        <PortfolioSnapshotsDialog wsId={activeWorkspaceId}
                                       onClose={() => setSnapshotsOpen(false)} />
      )}
      {alertBrand && (
        <PulseAlertConfigDialog wsId={activeWorkspaceId} brand={alertBrand}
                                     onClose={() => setAlertBrand(null)}
                                     onSaved={() => { setAlertBrand(null); load(); }} />
      )}
      {alertsSettingsOpen && (
        <PulseAlertsSettingsDialog wsId={activeWorkspaceId}
                                        onClose={() => setAlertsSettingsOpen(false)} />
      )}
      {eventsOpen && (
        <PulseAlertEventsDialog wsId={activeWorkspaceId}
                                     onClose={() => setEventsOpen(false)} />
      )}
    </div>
  );
}
